/**
 * Centralized API endpoint paths for the Customer portal.
 *
 * Paths are RELATIVE to `VITE_API_BASE_URL` (configured per environment and
 * consumed by `apiClient` in `./client`).
 *
 * Contract: `docs/api/Waset Management.openapi-full.json` (Phase 5+).
 */
export const endpoints = {
  auth: {
    login: "/auth/login",
    // Customer registration paths from the auth contract.
    register: "/auth/register/customer",
    registerOrganization: "/auth/register/customer-org",
    verifyEmail: "/auth/verify-email",
    // NOTE: no resend-verification endpoint exists in the published collection
    // yet; this is a best-effort path pending the real spec.
    resendOtp: "/auth/resend-otp",
    refresh: "/auth/refresh",
    logout: "/auth/logout",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    changePassword: "/auth/change-password",
    contextSwitch: "/auth/context/switch",
    contexts: "/auth/contexts",
    me: "/auth/me",
    acceptInvitation: "/auth/invitations/accept",
  },
  dashboard: {
    summary: "/dashboard",
  },
  requests: {
    list: "/customer/requests",
    create: "/customer/requests",
    summary: (requestId: string) => `/customer/requests/${requestId}`,
    update: (requestId: string) => `/customer/requests/${requestId}`,
    submit: (requestId: string) => `/customer/requests/${requestId}/submit`,
    cancel: (requestId: string) => `/customer/requests/${requestId}/cancel`,
    rematch: (requestId: string) => `/customer/requests/${requestId}/rematch`,
    switchToBidding: (requestId: string) => `/customer/requests/${requestId}/switch-to-bidding`,
    reopenBidding: (requestId: string) => `/customer/requests/${requestId}/reopen-bidding`,
    eligibleVendors: (requestId: string) => `/customer/requests/${requestId}/eligible-vendors`,
    selectVendors: (requestId: string) => `/customer/requests/${requestId}/select-vendors`,
    status: (requestId: string) => `/customer/requests/${requestId}/status`,
    bids: (requestId: string) => `/customer/requests/${requestId}/bids`,
    selectBid: (requestId: string, bidId: string) =>
      `/customer/requests/${requestId}/bids/${bidId}/select`,
  },
  orders: {
    list: "/customer/orders",
    summary: (orderId: string) => `/customer/orders/${orderId}`,
    cancel: (orderId: string) => `/customer/orders/${orderId}/cancel`,
    workOrders: (orderId: string) => `/customer/orders/${orderId}/work-orders`,
    payments: (orderId: string) => `/orders/${orderId}/payments`,
    initiatePayment: (orderId: string) => `/orders/${orderId}/payments/initiate`,
  },
  payments: {
    detail: (paymentId: string) => `/payments/${paymentId}`,
  },
  files: {
    upload: "/files",
    detail: (fileId: string) => `/files/${fileId}`,
  },
  wallet: {
    balance: "/wallet",
    transactions: "/wallet/transactions",
    topups: "/wallet/topups",
    topup: (id: string) => `/wallet/topups/${id}`,
    spendingLimits: "/organizations/wallet/spending-limits",
    spendingLimit: (membershipId: string) =>
      `/organizations/wallet/spending-limits/${membershipId}`,
  },
  invoices: {
    detail: (invoiceId: string) => `/invoices/${invoiceId}`,
    pdf: (invoiceId: string) => `/invoices/${invoiceId}/pdf`,
  },
  projectLocations: {
    list: "/customer/project-locations",
    create: "/customer/project-locations",
    detail: (savedLocationId: string) => `/customer/project-locations/${savedLocationId}`,
    update: (savedLocationId: string) => `/customer/project-locations/${savedLocationId}`,
    remove: (savedLocationId: string) => `/customer/project-locations/${savedLocationId}`,
  },
  services: {
    publicList: "/public/services",
    publicDetail: (serviceId: string) => `/public/services/${serviceId}`,
  },
  geography: {
    publicCountries: "/public/geography/countries",
    publicMunicipalities: "/public/geography/municipalities",
    publicDistricts: "/public/geography/districts",
  },
  catalog: {
    publicDeliveryTimeSlots: "/public/delivery-time-slots",
  },
  notifications: {
    list: "/notifications",
    markRead: (notificationId: string) => `/notifications/${notificationId}/read`,
  },
  organizations: {
    invitations: (orgId: string) => `/organizations/${orgId}/invitations`,
    invitation: (orgId: string, invitationId: string) =>
      `/organizations/${orgId}/invitations/${invitationId}`,
    roles: (orgId: string) => `/organizations/${orgId}/roles`,
    assignablePermissions: (orgId: string) =>
      `/organizations/${orgId}/modules/assignable-permissions`,
    // Not in the published OpenAPI yet; shape from the backend B2B invitations-and-roles note.
    memberRoles: (orgId: string, membershipId: string) =>
      `/organizations/${orgId}/members/${membershipId}/roles`,
  },
} as const;
